import { isAlertWithin48Hours } from '@/Components/Common/deviceUtils';
import DashboardIcon from '@/Components/Common/Icons/DashboardIcon.svg';
import DashboardExportButton from '@/Components/Dashboard/DashboardExportButton';
import useConfig from '@/Components/Hooks/useConfig';
import useGetUserAccessories from '@/Components/Hooks/useGetUserAccessories';
import useLocationTranslation from '@/Components/Hooks/useLocationTranslation';
import { ACCESSORIES_PROPERTIES, HC200_MODEL_NUMBER, USER_MEASUREMENTS } from '@/constants/device';
import { DASHBOARD_REPORT_TYPES, exportDashboardToPDF } from '@/utilities/helpers/fileHelper';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getUserHubs, getUserInfo } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { getUsername } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { find, get } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AlertTable from './AlertsTable/AlertTable';
import './Dashboard.less';
import {
  AlertsHistoryTable,
  DashboardHeader,
  DashboardTabs,
  DashboardTitle,
  DashboardTitleWrapper,
} from './Dashboard.styles';
import {
  DashboardModalContext,
  DashboardReportContext,
  DashboardSSEEventContext,
} from './DashboardContext';
import { DashboardModal } from './DashboardModal';
import Home from './Home/Home';
import { ALERT_HISTORY_DAYS, useAlertEventsStream, useDashboardEventStream } from './hooks';
import Rules from './Rules/Rules';
import { formatTableData, getSupportedCategoriesFromConfig, USER_STATE_LIST } from './utils';

const DASHBOARD_TABS = { HOME: 'home', ALERTS: 'alerts', RULES: 'rules' };

const Dashboard = ({ timezone, loginUserTimeformat, hubRecord, loginUserMeasurementUnit }) => {
  const trans = useSelector(translator);
  const currentUser = useSelector(getCurrentUser);
  const { locale = 'en-US' } = useSelector(getUserInfo(currentUser?.useruuid));
  const useruuid = hubRecord?.useruuid || '';
  const accountInfo = useSelector(getUserInfo(useruuid));
  const accountListingHubs = useSelector(getUserHubs(currentUser?.useruuid));
  // const locale = hubRecord?.locale || 'en-US';
  const measurementSystem = loginUserMeasurementUnit?.measurement || USER_MEASUREMENTS.IMPERIAL;
  const is24HourFormat = Boolean(loginUserTimeformat?.is24hoursformat);
  const timezoneString = timezone
    ? timezone.split(' ')[0]
    : Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [activeTab, setActiveTab] = useState(DASHBOARD_TABS.HOME);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  // Callback ref: the slot node is re-created every time the Rules tab is re-selected,
  // so it must live in state for Rules to re-render its portal into the new node.
  const [rulesHeaderActionsSlot, setRulesHeaderActionsSlot] = useState(null);

  const { data: currentUserConfig = {}, isLoading: isConfigLoading } = useConfig({ useruuid });

  const supportedCategories = useMemo(
    () => getSupportedCategoriesFromConfig(currentUserConfig),
    [currentUserConfig]
  );

  const openDashboardModal = (config) => {
    setModalConfig(config);
    setIsInfoModalOpen(true);
  };

  const closeDashboardModal = () => {
    modalConfig?.onClose?.();
    setIsInfoModalOpen(false);
    setModalConfig(null);
  };

  const { data: accessories = [] } = useGetUserAccessories(hubRecord);

  const hc200DeviceUuid = useMemo(() => {
    const hc200Accessory = find(accessories, [
      ACCESSORIES_PROPERTIES.MODEL_NUMBER,
      HC200_MODEL_NUMBER,
    ]);
    return get(hc200Accessory, ACCESSORIES_PROPERTIES.DEVICE_UUID, '');
  }, [accessories]);

  const isAlertsApplicable = useMemo(() => {
    const selectedListingHub = accountListingHubs?.find((hub) => hub?.useruuid === useruuid);
    const listingAlerts = selectedListingHub?.alerts;
    if (Array.isArray(listingAlerts)) {
      return listingAlerts.some(isAlertWithin48Hours);
    }
    return Array.isArray(hubRecord?.alerts) && hubRecord.alerts.some(isAlertWithin48Hours);
  }, [accountListingHubs, useruuid, hubRecord.alerts]);

  const { latestEvent, eventStream } = useDashboardEventStream({
    useruuid,
    supportedCategories,
    isConfigLoading,
  });

  // ── PDF report support ─────────────────────────────────────────────────────
  const accountName = useMemo(
    () => getUsername(accountInfo) || getUsername(hubRecord) || '',
    [accountInfo, hubRecord]
  );

  // Home sections register their report-ready data here. The ref is retained
  // across tab switches so the export works regardless of the active tab.
  const reportSectionsRef = useRef({});
  const registerReportSection = useCallback((key, payload) => {
    reportSectionsRef.current[key] = payload;
  }, []);
  const reportContextValue = useMemo(() => ({ registerReportSection }), [registerReportSection]);

  // Stream the alert history window (ALERTS_HISTORY_NUMBER_OF_DAYS) centrally so the full alert
  // history is available for the report without requiring the Alerts tab to be visited.
  // AlertTable uses the same hook and window, so both share one request and its cached result.
  const { translateLocation } = useLocationTranslation(useruuid);
  const { data: reportAlerts } = useAlertEventsStream({
    useruuid,
    hc200DeviceUuid,
    supportedCategories,
    days: ALERT_HISTORY_DAYS,
  });

  const handleExportReport = useCallback(
    async (reportType) => {
      const sections = reportSectionsRef.current || {};
      const isDetail = reportType === DASHBOARD_REPORT_TYPES.DETAIL;

      const reportAlertRows = formatTableData(
        reportAlerts || [],
        timezoneString,
        locale,
        trans,
        is24HourFormat,
        accessories,
        translateLocation
      );

      // Format the alerts actually shown on the dashboard (includes real-time SSE
      // alerts that may not be in the API history) and merge them with the alert
      // history so the report never loses information. Newest first, de-duplicated.
      const displayedAlertRows = formatTableData(
        sections.alerts || [],
        timezoneString,
        locale,
        trans,
        is24HourFormat,
        accessories,
        translateLocation
      );
      const mergedById = new Map();
      [...displayedAlertRows, ...reportAlertRows].forEach((row) => {
        const dedupeKey = row.key || `${row.utc || ''}|${row.category || ''}|${row.ruleName || ''}`;
        if (!mergedById.has(dedupeKey)) mergedById.set(dedupeKey, row);
      });
      const mergedAlertRows = [...mergedById.values()].sort(
        (a, b) => new Date(b.utc || 0) - new Date(a.utc || 0)
      );
      const topAlerts =
        mergedAlertRows.length > 0 ? mergedAlertRows.slice(0, 5) : displayedAlertRows;

      await exportDashboardToPDF({
        trans,
        reportType,
        accountName,
        currentUser,
        topAlerts,
        alertHistory: isDetail ? mergedAlertRows : [],
        recentActivity: sections.recentActivity || [],
        taskCompliance: sections.taskCompliance || null,
        schedule: isDetail ? sections.schedule || [] : [],
        weather: isDetail ? sections.weather || null : null,
      });
    },
    [
      trans,
      accountName,
      currentUser,
      reportAlerts,
      timezoneString,
      locale,
      is24HourFormat,
      accessories,
      translateLocation,
    ]
  );

  return (
    <>
      <DashboardSSEEventContext.Provider value={{ latestEvent, eventStream }}>
        <DashboardModalContext.Provider value={{ openDashboardModal, closeDashboardModal }}>
          <DashboardReportContext.Provider value={reportContextValue}>
            <DashboardHeader>
              <DashboardTitleWrapper>
                <DashboardIcon />
                <DashboardTitle>{trans('DASHBOARD')}</DashboardTitle>
              </DashboardTitleWrapper>
              <DashboardExportButton onExport={handleExportReport} disabled={!useruuid} />
            </DashboardHeader>

            <DashboardTabs
              activeKey={activeTab}
              onChange={setActiveTab}
              tabBarExtraContent={
                activeTab === DASHBOARD_TABS.RULES
                  ? { right: <div ref={setRulesHeaderActionsSlot} /> }
                  : undefined
              }
              items={[
                {
                  key: DASHBOARD_TABS.HOME,
                  label: trans('HOME'),
                  children: (
                    <Home
                      timezone={timezoneString}
                      is24HourFormat={is24HourFormat}
                      useruuid={useruuid}
                      locale={locale}
                      hubRecord={hubRecord}
                      measurementSystem={measurementSystem}
                      hc200DeviceUuid={hc200DeviceUuid}
                      accessories={accessories}
                      userStateList={USER_STATE_LIST}
                      isAlertsApplicable={isAlertsApplicable}
                      redirectToAlertsTab={() => setActiveTab(DASHBOARD_TABS.ALERTS)}
                      supportedCategories={supportedCategories}
                    />
                  ),
                },
                {
                  key: DASHBOARD_TABS.ALERTS,
                  label: trans('ALERTS'),
                  children: (
                    <AlertsHistoryTable>
                      <AlertTable
                        key={`${useruuid}-${hc200DeviceUuid}`}
                        useruuid={useruuid}
                        hc200DeviceUuid={hc200DeviceUuid}
                        timezone={timezoneString}
                        locale={locale}
                        is24hours={is24HourFormat}
                        userAccessories={accessories}
                        supportedCategories={supportedCategories}
                      />
                    </AlertsHistoryTable>
                  ),
                },
                {
                  key: DASHBOARD_TABS.RULES,
                  label: trans('RULES'),
                  children: (
                    <Rules
                      useruuid={useruuid}
                      measurementSystem={measurementSystem}
                      is24HourFormat={is24HourFormat}
                      timezone={timezoneString}
                      accessories={accessories}
                      supportedCategories={supportedCategories}
                      headerActionsSlot={rulesHeaderActionsSlot}
                    />
                  ),
                },
              ]}
            />
          </DashboardReportContext.Provider>
        </DashboardModalContext.Provider>
      </DashboardSSEEventContext.Provider>

      <DashboardModal
        open={isInfoModalOpen && !!modalConfig}
        onOpen={modalConfig?.onOpen}
        onClose={modalConfig?.onClose}
        onOpenChange={modalConfig?.onOpenChange}
        onCancel={closeDashboardModal}
        footer={modalConfig?.footer || null}
        width={'auto'}
        destroyOnClose
        title={modalConfig?.title || null}
      >
        {modalConfig?.content || null}
      </DashboardModal>
    </>
  );
};

Dashboard.propTypes = {
  timezone: PropTypes.string,
  loginUserTimeformat: PropTypes.shape({
    is24hoursformat: PropTypes.bool,
  }),
  hubRecord: PropTypes.shape({
    useruuid: PropTypes.string,
    locale: PropTypes.string,
    alerts: PropTypes.array,
  }),
  loginUserMeasurementUnit: PropTypes.shape({
    measurement: PropTypes.string,
  }),
};

export default Dashboard;
